import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { StatePanel } from './components/StatePanel';
import {
  DEFAULT_APP_PAGE_ID,
  buildAppPagePath,
  resolveAppPage,
  type AppPageId,
  type AppPageRenderContext,
} from './config/appPages';
import { useLanguage } from './hooks/useLanguage';
import { fetchMistakeTrainingVerbs, MISTAKE_RETRAINING_SESSION_SIZE } from './mistakes-store';
import type { CompletedTrainingPayload, TrainingResultsState } from './lib/trainingSession';
import type { SessionMode, TrainingMode, TrainingSource, User } from './types';

const AppLayout = lazy(() => import('./components/AppLayout').then((module) => ({ default: module.AppLayout })));
const SignInForm = lazy(() => import('./components/auth/SignInForm').then((module) => ({ default: module.SignInForm })));
const FlashcardsTraining = lazy(() =>
  import('./components/pages/FlashcardsTraining').then((module) => ({ default: module.FlashcardsTraining }))
);
const Landing = lazy(() => import('./components/pages/Landing').then((module) => ({ default: module.Landing })));
const MultipleChoiceTraining = lazy(() =>
  import('./components/pages/MultipleChoiceTraining').then((module) => ({ default: module.MultipleChoiceTraining }))
);
const NotFoundPage = lazy(() => import('./components/pages/NotFound').then((module) => ({ default: module.NotFoundPage })));
const SpeedVerbsTraining = lazy(() =>
  import('./components/pages/SpeedVerbsTraining').then((module) => ({ default: module.SpeedVerbsTraining }))
);
const TrainingResults = lazy(() =>
  import('./components/pages/TrainingResults').then((module) => ({ default: module.TrainingResults }))
);
const TypingTraining = lazy(() =>
  import('./components/pages/TypingTraining').then((module) => ({ default: module.TypingTraining }))
);

type TrainingCompletionRoutePayload = CompletedTrainingPayload & { source: TrainingSource };
type RoutedTrainingResultsState = TrainingResultsState & { source: TrainingSource };

interface AppRoutesProps {
  loading: boolean;
  isAuthenticated: boolean;
  activeUser: User | null;
  trainingResults: RoutedTrainingResultsState | null;
  onSignIn: (email: string, password: string) => Promise<void>;
  onSignUp: (displayName: string, email: string, password: string) => Promise<void>;
  onGoogleSignIn: () => Promise<void>;
  onForgotPassword: (email: string) => Promise<void>;
  onLogout: () => Promise<void>;
  onTrainingComplete: (payload: TrainingCompletionRoutePayload) => void;
  onClearTrainingResults: () => void;
}

interface AppPageRouteProps {
  activeUser: User;
  isAdmin: boolean;
  onPageChange: (page: AppPageId) => void;
  onLogout: () => Promise<void>;
  onStartTraining: (mode: TrainingMode, sessionMode?: SessionMode, source?: TrainingSource) => void;
  onViewMistakes: () => void;
  onViewLeaderboard: () => void;
}

interface TrainingModeRouteProps {
  activeUser: User | null;
  onComplete: (payload: TrainingCompletionRoutePayload) => void;
  onExit: () => void;
}

interface TrainingResultsRouteProps {
  trainingResults: RoutedTrainingResultsState | null;
  onContinue: () => void;
  onRepeat: () => void;
}

interface AuthGuardProps {
  isAuthenticated: boolean;
  children: ReactNode;
}

interface RouteSuspenseProps {
  children: ReactNode;
  fullScreen?: boolean;
}

function isTrainingMode(value: string | undefined): value is TrainingMode {
  return value === 'typing' || value === 'multiple-choice' || value === 'flashcards' || value === 'speed-verbs';
}

function buildTrainingPath(mode: TrainingMode, sessionMode: SessionMode, source: TrainingSource = 'default') {
  const searchParams = new URLSearchParams({ sessionMode });

  if (source === 'mistakes') {
    searchParams.set('source', source);
  }

  return `/training/${mode}?${searchParams.toString()}`;
}

function RouteFallback({ fullScreen = true }: { fullScreen?: boolean }) {
  const { t } = useLanguage();

  return (
    <StatePanel
      state="loading"
      size={fullScreen ? 'default' : 'compact'}
      title={t('common.loading')}
      className={fullScreen ? 'min-h-screen rounded-none border-0 bg-transparent' : 'min-h-[40vh]'}
    />
  );
}

function RouteSuspense({ children, fullScreen = true }: RouteSuspenseProps) {
  return <Suspense fallback={<RouteFallback fullScreen={fullScreen} />}>{children}</Suspense>;
}

function ProtectedRoute({ isAuthenticated, children }: AuthGuardProps) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicOnlyRoute({ isAuthenticated, children }: AuthGuardProps) {
  if (isAuthenticated) {
    return <Navigate to={buildAppPagePath(DEFAULT_APP_PAGE_ID)} replace />;
  }

  return <>{children}</>;
}

function AppPageRoute({
  activeUser,
  isAdmin,
  onPageChange,
  onLogout,
  onStartTraining,
  onViewMistakes,
  onViewLeaderboard,
}: AppPageRouteProps) {
  const { page } = useParams<{ page: string }>();
  const pageConfig = resolveAppPage(page, isAdmin);

  if (!pageConfig) {
    return (
      <RouteSuspense>
        <NotFoundPage />
      </RouteSuspense>
    );
  }

  const renderContext: AppPageRenderContext = {
    activeUser,
    onStartTraining,
    onViewMistakes,
    onViewLeaderboard,
  };

  return (
    <RouteSuspense>
      <AppLayout
        user={activeUser}
        currentPage={pageConfig.id}
        onPageChange={onPageChange}
        onLogout={onLogout}
        isAdmin={isAdmin}
      >
        <RouteSuspense fullScreen={false}>{pageConfig.render(renderContext)}</RouteSuspense>
      </AppLayout>
    </RouteSuspense>
  );
}

function TrainingModeRoute({ activeUser, onComplete, onExit }: TrainingModeRouteProps) {
  const { mode } = useParams<{ mode: string }>();
  const [searchParams] = useSearchParams();
  const sessionMode: SessionMode = searchParams.get('sessionMode') === 'test' ? 'test' : 'training';
  const source: TrainingSource = searchParams.get('source') === 'mistakes' ? 'mistakes' : 'default';
  const trainingVerbs =
    source === 'mistakes' && activeUser?.uid
      ? fetchMistakeTrainingVerbs(activeUser.uid, { limit: MISTAKE_RETRAINING_SESSION_SIZE })
      : [];

  if (!isTrainingMode(mode)) {
    return (
      <RouteSuspense>
        <NotFoundPage />
      </RouteSuspense>
    );
  }

  if (source === 'mistakes' && trainingVerbs.length === 0) {
    return <Navigate to={buildAppPagePath('mistakes')} replace />;
  }

  const handleComplete = (payload: Omit<CompletedTrainingPayload, 'mode' | 'sessionMode' | 'source'>) => {
    onComplete({
      ...payload,
      mode,
      sessionMode,
      source,
    });
  };

  return (
    <RouteSuspense>
      {mode === 'typing' ? (
        <TypingTraining onComplete={handleComplete} onExit={onExit} mode={sessionMode} verbs={trainingVerbs} />
      ) : mode === 'multiple-choice' ? (
        <MultipleChoiceTraining onComplete={handleComplete} onExit={onExit} verbs={trainingVerbs} />
      ) : mode === 'speed-verbs' ? (
        <SpeedVerbsTraining onComplete={handleComplete} onExit={onExit} verbs={trainingVerbs} />
      ) : (
        <FlashcardsTraining onComplete={handleComplete} onExit={onExit} verbs={trainingVerbs} />
      )}
    </RouteSuspense>
  );
}

function TrainingResultsRoute({ trainingResults, onContinue, onRepeat }: TrainingResultsRouteProps) {
  if (!trainingResults) {
    return <Navigate to={buildAppPagePath(DEFAULT_APP_PAGE_ID)} replace />;
  }

  return (
    <RouteSuspense>
      <TrainingResults
        results={trainingResults}
        source={trainingResults.source}
        xpEarned={trainingResults.xpEarned}
        onContinue={onContinue}
        onRepeat={onRepeat}
      />
    </RouteSuspense>
  );
}

export function AppRoutes({
  loading,
  isAuthenticated,
  activeUser,
  trainingResults,
  onSignIn,
  onSignUp,
  onGoogleSignIn,
  onForgotPassword,
  onLogout,
  onTrainingComplete,
  onClearTrainingResults,
}: AppRoutesProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isAdmin = false;

  if (loading) {
    return (
      <StatePanel
        state="loading"
        title={t('app.loading')}
        className="min-h-screen rounded-none border-0 bg-transparent"
      />
    );
  }

  const handlePageChange = (page: AppPageId) => {
    navigate(buildAppPagePath(page));
  };

  const handleStartTraining = (
    mode: TrainingMode,
    sessionMode: SessionMode = 'training',
    source: TrainingSource = 'default',
  ) => {
    navigate(buildTrainingPath(mode, sessionMode, source));
  };

  const handleViewMistakes = () => {
    handlePageChange('mistakes');
  };

  const handleViewLeaderboard = () => {
    handlePageChange('leaderboard');
  };

  const handleLogout = async () => {
    await onLogout();
    navigate('/');
  };

  const handleTrainingComplete = (payload: TrainingCompletionRoutePayload) => {
    onTrainingComplete(payload);
    navigate('/training/results');
  };

  const handleTrainingExit = () => {
    navigate(buildAppPagePath(DEFAULT_APP_PAGE_ID));
  };

  const handleContinueFromResults = () => {
    onClearTrainingResults();
    navigate(buildAppPagePath(DEFAULT_APP_PAGE_ID));
  };

  const handleRepeatTraining = () => {
    if (!trainingResults) {
      navigate(buildAppPagePath(DEFAULT_APP_PAGE_ID));
      return;
    }

    navigate(buildTrainingPath(trainingResults.mode, trainingResults.sessionMode, trainingResults.source), { replace: true });
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicOnlyRoute isAuthenticated={isAuthenticated}>
            <RouteSuspense>
              <Landing onGetStarted={() => navigate('/login')} />
            </RouteSuspense>
          </PublicOnlyRoute>
        }
      />

      <Route
        path="/login"
        element={
          <PublicOnlyRoute isAuthenticated={isAuthenticated}>
            <RouteSuspense>
              <SignInForm
                onSignIn={onSignIn}
                onSignUp={onSignUp}
                onGoogleSignIn={onGoogleSignIn}
                onForgotPassword={onForgotPassword}
              />
            </RouteSuspense>
          </PublicOnlyRoute>
        }
      />

      <Route path="/app" element={<Navigate to={buildAppPagePath(DEFAULT_APP_PAGE_ID)} replace />} />

      <Route
        path="/app/:page"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            {activeUser ? (
              <AppPageRoute
                activeUser={activeUser}
                isAdmin={isAdmin}
                onPageChange={handlePageChange}
                onLogout={handleLogout}
                onStartTraining={handleStartTraining}
                onViewMistakes={handleViewMistakes}
                onViewLeaderboard={handleViewLeaderboard}
              />
            ) : (
              <Navigate to="/login" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/training/:mode"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <TrainingModeRoute activeUser={activeUser} onComplete={handleTrainingComplete} onExit={handleTrainingExit} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/training/results"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <TrainingResultsRoute
              trainingResults={trainingResults}
              onContinue={handleContinueFromResults}
              onRepeat={handleRepeatTraining}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="*"
        element={
          <RouteSuspense>
            <NotFoundPage />
          </RouteSuspense>
        }
      />
    </Routes>
  );
}


